<script setup lang="ts">
import type {ProductResponse} from "../../../responses/ProductResponse";
import {computed, type HTMLAttributes, type Ref, ref} from "vue";
import {tw} from "../../../utils/tailwind";
import {Money} from "../../../utils/money";
import {useCartStore} from "../../stores/cart";
import {first} from "lodash";
import {label} from "../../mixins/translations";
import FavoriteButton from "./FavoriteButton.vue";

interface Props {
    products: ProductResponse[],
    class?: HTMLAttributes['class'],
}

const props = defineProps<Props>();
const cartStore = useCartStore();
const toggleAll: Ref<Boolean> = ref(false);
const addToCart = (product: ProductResponse) => {
    cartStore.addItem({
        amount: 1,
        variant_id: first(product.variants).id
    }).then(response => {
        // window.trackAddToCartGoogle(this.product, this.amount, 'add_to_cart');
    });
}
</script>
<template>
    <div class="mt-5 border-t border-gray-200 pt-5">
        <h3 class="w-full font-bold text-lg mb-5">
            {{ label('site.product.related_products.title') }}
        </h3>
        <div class="w-full mb-2 flex flex-col" v-for="(product, index) in products" :class="{'hidden': index > 3 && !toggleAll}">
            <div class="flex flex-col lg:flex-row gap-3 items-start lg:items-center p-3 bg-[#F8F7F4]">

                <!-- product blok -->
                <div class="w-full flex gap-3">
                    <div class="w-1/3 sm:w-1/4 lg:w-1/5">
                        <a :href="`/${product.slug}/`">
                            <img
                                :src="product.thumbnail?.image_url + `?w=100&h=100&fit=fill&q=80&fm=webp`"
                                class="object-center aspect-square w-full"
                                :alt="product.thumbnail?.alt_text"
                            >
                        </a>
                    </div>
                    <div class="flex-1 flex justify-between items-center px-3 gap-x-2">
                        <h3 class="text-sm sm:text-base font-medium">
                            <a :href="`/${product.slug}/`">{{ product.name }}</a>
                        </h3>
                        <span class="text-gray-500 whitespace-nowrap" v-show="product.price">
                            {{ Money.format(product.price?.price_incl, product.price?.currency) }}
                        </span>
                    </div>
                </div>

                <!-- knoppenbalk -->
                <div class="flex flex-row gap-2 mt-3 lg:mt-0 self-end lg:self-center lg:ml-auto">
                    <!-- add to cart -->
                    <a v-if="product.price"
                       @click.prevent="addToCart(product)"
                       class="font-medium border border-gray-200 text-base px-3 py-2 leading-6 text-black transition rounded-lg bg-success hover:bg-success-700 flex items-center justify-center cursor-pointer">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="h-6 w-6"
                             stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="8" cy="21" r="1"/>
                            <circle cx="19" cy="21" r="1"/>
                            <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
                        </svg>
                    </a>
                    <a v-else :href="`/${product.slug}/`"
                       class="font-medium border border-gray-200 text-base px-3 py-2 leading-6 text-black transition rounded-lg flex bg-white hover:bg-gray-200 items-center justify-center cursor-pointer">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                    </a>


                    <!-- favorite -->
                    <favorite-button
                        icon-class="cursor-pointer font-medium border border-gray-200 text-base px-3 py-2 leading-6 text-black transition rounded-lg bg-white hover:bg-gray-200 flex items-center justify-center"
                        :product="product">
                    </favorite-button>

                    <!-- CUSTOMABLE -->
                    <a :href="`/${product.slug}/`"
                       class="font-medium border border-gray-200 text-base px-3 py-2 leading-6 text-black transition rounded-lg bg-white hover:bg-gray-200 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-pencil-ruler-icon lucide-pencil-ruler">
                            <path d="M13 7 8.7 2.7a2.41 2.41 0 0 0-3.4 0L2.7 5.3a2.41 2.41 0 0 0 0 3.4L7 13"/><path d="m8 6 2-2"/><path d="m18 16 2-2"/><path d="m17 11 4.3 4.3c.94.94.94 2.46 0 3.4l-2.6 2.6c-.94.94-2.46.94-3.4 0L11 17"/><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/>
                        </svg>
                    </a>
                </div>
            </div>
        </div>

        <div class="w-full text-center justify-center mt-4 items-center" v-show="products.length > 4">
            <a href="javascript:" class=" text-center text-lg flex flex-col items-center justify-center" v-show="!toggleAll"
               @click.prevent="toggleAll = true;">
                <span class="text-base">{{ label('site.product.related_products.show_more') }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-down-icon flex lucide-chevron-down w-6 h-6"><path d="m6 9 6 6 6-6"/></svg>
            </a>
            <a href="javascript:" class="text-center text-lg flex flex-col items-center justify-center" v-show="toggleAll"
               @click.prevent="toggleAll = false;">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-up-icon flex lucide-chevron-up w-6 h-6"><path d="m18 15-6-6-6 6"/></svg>
                <span class="text-base">{{ label('site.product.related_products.show_less') }}</span>
            </a>
        </div>
    </div>
</template>
